// Instagram desde el Chat — ADR-2026-09-24-instagram-en-el-chat.
//
// El comerciante le pregunta al agente cómo le va en SU Instagram y el agente contesta
// con los números reales que da Meta (alcance, vistas, guardados, público), que el
// scraping no ve. Es sólo lectura.
//
// Mismo molde que `tiendanubeEnElChat.ts`: nada de React ni de red acá adentro; lo que
// necesita el mundo exterior se inyecta, así cada regla se prueba sola. Y la misma
// puerta: las herramientas viajan por `herramientasExtra` SÓLO si la marca activa tiene
// Instagram conectado, para no cobrarle el prefijo de cada turno a quien no puede usarlas.
//
// Los números se formatean ACÁ y no en el modelo: le llegan escritos como se los tiene
// que contar a la persona (con el período, la comparación y lo que falta), así no tiene
// que hacer cuentas ni inventar un 0 donde Meta no mandó dato.

import type { HerramientaChat } from './tipos';
import type { EstadoDeInstagram } from '../lib/instagramConexion';
import type { PedidoDeMetricas, RespuestaDeMetricas } from '../lib/instagramMetricas';

export const NOMBRE_VER_METRICAS_DE_INSTAGRAM = 'ver_metricas_de_instagram';
export const NOMBRE_VER_POSTS_DE_INSTAGRAM = 'ver_posts_de_instagram';
export const NOMBRE_VER_PUBLICO_DE_INSTAGRAM = 'ver_publico_de_instagram';

const GRATIS =
  'Es gratis (no cuesta créditos) y son los datos oficiales de Meta de SU cuenta conectada: no la uses para otras cuentas.';

export const HERRAMIENTA_VER_METRICAS_DE_INSTAGRAM: HerramientaChat = {
  name: NOMBRE_VER_METRICAS_DE_INSTAGRAM,
  description:
    `Cómo le fue a la cuenta de Instagram del usuario en un período: alcance, vistas, interacciones, me gusta, comentarios, compartidos, guardados, toques en los links, seguidores nuevos y los que dejaron de seguir, cada uno comparado con el período anterior. ${GRATIS} El período máximo es 30 días.`,
  input_schema: {
    type: 'object',
    properties: {
      dias: {
        type: 'integer',
        description: 'Cuántos días mirar hacia atrás, de 1 a 30. Por defecto 28. "Esta semana" = 7, "este mes" = 30.',
      },
    },
  },
};

export const HERRAMIENTA_VER_POSTS_DE_INSTAGRAM: HerramientaChat = {
  name: NOMBRE_VER_POSTS_DE_INSTAGRAM,
  description:
    `Los posts y reels de la cuenta de Instagram del usuario con sus números (vistas, alcance, interacciones, guardados, compartidos, me gusta, comentarios y, en los reels, el tiempo promedio visto), con su link. Sirve para "mis mejores posts", "cómo le fue al último reel" o "qué funciona mejor". ${GRATIS} Las historias no están.`,
  input_schema: {
    type: 'object',
    properties: {
      orden: {
        type: 'string',
        enum: ['recientes', 'vistas', 'alcance', 'interacciones', 'guardados', 'compartidos'],
        description: 'Por defecto "recientes". Para "los mejores", elegí la métrica que importa; si no dice cuál, "vistas".',
      },
      cantidad: { type: 'integer', description: 'Cuántos posts, de 1 a 30. Por defecto 10.' },
      dias: {
        type: 'integer',
        description: 'Opcional: sólo los publicados en los últimos N días (1 a 30).',
      },
    },
  },
};

export const HERRAMIENTA_VER_PUBLICO_DE_INSTAGRAM: HerramientaChat = {
  name: NOMBRE_VER_PUBLICO_DE_INSTAGRAM,
  description:
    `Quiénes siguen la cuenta de Instagram del usuario: principales países, ciudades, edades y género, con porcentajes. Meta sólo lo da con 100 seguidores o más. ${GRATIS}`,
  input_schema: { type: 'object', properties: {} },
};

export const NOMBRE_CONECTAR_INSTAGRAM = 'conectar_instagram';

/**
 * La ÚNICA que viaja cuando la marca NO tiene Instagram conectado (o se le venció), y la
 * contracara de las otras tres. Como `conectar_tienda`: dibuja una tarjeta con el botón
 * a Mi marca y no navega, porque cambiarle la pantalla a alguien que está leyendo la
 * respuesta está mal. Autorizar lo hace la persona en la ventana de Instagram.
 */
export const HERRAMIENTA_CONECTAR_INSTAGRAM: HerramientaChat = {
  name: NOMBRE_CONECTAR_INSTAGRAM,
  description:
    'Muestra en el chat una tarjeta con el botón para ir a conectar su Instagram en Mi marca. Usala cuando pregunte por los números de SU Instagram (cómo le fue, mejores posts, seguidores) y vos NO tengas las herramientas de Instagram: eso significa que no lo tiene conectado o que se le venció. No lo lleva a ningún lado: el botón lo toca él. Contale en una línea qué va a poder ver (alcance, vistas, mejores posts, quién lo sigue; gratis y sin tocar su cuenta) y mostrá la tarjeta en la misma respuesta. Cuando termine y te escriba, ya vas a tener las otras herramientas.',
  input_schema: { type: 'object', properties: {} },
};

export const HERRAMIENTAS_DE_INSTAGRAM: HerramientaChat[] = [
  HERRAMIENTA_VER_METRICAS_DE_INSTAGRAM,
  HERRAMIENTA_VER_POSTS_DE_INSTAGRAM,
  HERRAMIENTA_VER_PUBLICO_DE_INSTAGRAM,
];

// ── El bloque de contexto ───────────────────────────────────────────────────

/**
 * La línea que viaja en el contexto de cada turno. Sale de nuestra base (la edge
 * `ig-conexion`), no de Meta. null = no se dice nada: sin conexión, o no se pudo averiguar.
 */
export function bloqueDeInstagram(
  estado: EstadoDeInstagram | null,
  /**
   * Estuvo conectado en algún momento de esta sesión y ahora no. Sin esto el modelo,
   * que ya vio las métricas más arriba en la charla, contesta "sí, lo tenés conectado"
   * de memoria (pasó probándolo el 24/09).
   */
  seDesconecto = false,
): string | null {
  if (!estado || estado.error) return null;
  const cuenta = estado.username ? `@${estado.username}` : 'su cuenta';
  if (!estado.conectada && seDesconecto && estado.estado !== 'vencida') {
    return `══════════ INSTAGRAM ══════════\nLa marca YA NO tiene Instagram conectado: se desconectó durante esta charla. Si antes viste sus números, son de cuando estaba conectado: no digas que sigue conectado ni los presentes como actuales. Si quiere verlo de nuevo, mostrale la tarjeta con \`${NOMBRE_CONECTAR_INSTAGRAM}\`.`;
  }
  if (!estado.conectada && estado.estado === 'vencida') {
    return `══════════ INSTAGRAM ══════════\nLa marca tenía conectado ${cuenta}, pero la conexión venció. Si pregunta por sus métricas, mostrale la tarjeta para reconectarla con \`${NOMBRE_CONECTAR_INSTAGRAM}\`. No uses los scrapers para su propia cuenta.`;
  }
  if (!estado.conectada) return null;
  return `══════════ INSTAGRAM ══════════\nLa marca tiene conectado ${cuenta} con permiso de Meta. Para SUS números usá \`${NOMBRE_VER_METRICAS_DE_INSTAGRAM}\`, \`${NOMBRE_VER_POSTS_DE_INSTAGRAM}\` y \`${NOMBRE_VER_PUBLICO_DE_INSTAGRAM}\`: son gratis y ven lo que los scrapers no (alcance, vistas, guardados, público). Los scrapers quedan para OTRAS cuentas. Antes de contar números cargá la skill \`instagram\`. No lo ofrezcas en cada respuesta: sólo cuando venga al caso.`;
}

const HERRAMIENTAS_QUE_LEEN = [
  NOMBRE_VER_METRICAS_DE_INSTAGRAM,
  NOMBRE_VER_POSTS_DE_INSTAGRAM,
  NOMBRE_VER_PUBLICO_DE_INSTAGRAM,
];

/**
 * ¿El agente leyó el Instagram en esta charla? Se mira en los registros que el motor
 * deja por cada herramienta usada ("[usaste ver_metricas_de_instagram …]"), así sobrevive
 * a recargar la página: si más arriba hay números y ahora no hay conexión, el agente
 * tiene que enterarse de que ya no valen.
 */
export function leyoInstagramEnLaCharla(mensajes: ReadonlyArray<{ registros?: string[] }>): boolean {
  return mensajes.some((m) => m.registros?.some((r) => HERRAMIENTAS_QUE_LEEN.some((h) => r.includes(h))));
}

// ── Formato ─────────────────────────────────────────────────────────────────

const numero = (n: unknown): string =>
  typeof n === 'number' ? n.toLocaleString('es-AR', { maximumFractionDigits: 1 }) : 'sin dato';

const fecha = (iso: unknown): string => {
  if (typeof iso !== 'string') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // `en-CA` da siempre AAAA-MM-DD; con `es-AR` cada motor de Intl formatea distinto
  // ("20/9" en Node, "20/09" en Chrome), y el agente vería fechas desparejas.
  const [, mes, dia] = d.toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).split('-');
  return `${dia}/${mes}`;
};

const cambio = (pct: unknown): string => {
  if (typeof pct !== 'number') return '';
  const signo = pct > 0 ? '+' : '';
  return `${signo}${pct.toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`;
};

type Json = Record<string, unknown>;

/** Lo que es problema de la cuenta, en palabras de la persona. null = hay datos. */
function problemaDeLaCuenta(r: RespuestaDeMetricas): string | null {
  if (!r.conectada) {
    return 'Esta marca no tiene Instagram conectado, así que no puedo ver sus números. No inventes cifras ni uses los scrapers para su propia cuenta.';
  }
  if (r.disponible === false) {
    return `${r.mensaje ?? 'Instagram no devolvió los números.'} Contáselo así, sin inventar cifras.`;
  }
  return null;
}

export function textoDeLaCuenta(r: RespuestaDeMetricas): string {
  const problema = problemaDeLaCuenta(r);
  if (problema) return problema;

  const c = (r.cuenta ?? {}) as Json;
  const p = (r.periodo ?? {}) as Json;
  const usuario = c.usuario ?? r.usuario;
  const lineas = [
    `Instagram ${usuario ? `@${usuario}` : ''} — últimos ${numero(p.dias)} días (del ${fecha(p.desde)} al ${fecha(p.hasta)}), comparado con los ${numero(p.dias)} días anteriores:`,
    `• Seguidores hoy: ${numero(c.seguidores)} · Publicaciones: ${numero(c.publicaciones)}`,
  ];
  for (const m of (r.metricas as Json[] | undefined) ?? []) {
    const antes = typeof m.anterior === 'number' ? ` (${cambio(m.variacion_pct) || 'antes'} vs ${numero(m.anterior)})` : '';
    lineas.push(`• ${m.nombre}: ${numero(m.valor)}${antes}`);
  }
  const sinDatos = (r.sin_datos as string[] | undefined) ?? [];
  if (sinDatos.length) {
    lineas.push(`Meta no mandó dato de: ${sinDatos.join(', ')}. No es 0: no digas un número.`);
  }
  if (r.aviso) lineas.push(String(r.aviso));
  return lineas.join('\n');
}

const TIPO: Record<string, string> = { reel: 'Reel', post: 'Post', historia: 'Historia', anuncio: 'Anuncio' };

function lineaDePost(post: Json, n?: number): string {
  const partes = [
    ['vistas', post.vistas],
    ['alcance', post.alcance],
    ['interacciones', post.interacciones],
    ['guardados', post.guardados],
    ['compartidos', post.compartidos],
    ['me gusta', post.me_gusta],
    ['comentarios', post.comentarios],
  ]
    .filter(([, v]) => typeof v === 'number')
    .map(([nombre, v]) => `${numero(v)} ${nombre}`);
  if (typeof post.segundos_promedio_vistos === 'number') {
    partes.push(`${numero(post.segundos_promedio_vistos)} s vistos en promedio`);
  }
  const cabeza = `${n ? `${n}. ` : ''}${TIPO[String(post.tipo)] ?? 'Post'} (${post.formato}) del ${fecha(post.fecha)}`;
  const texto = post.texto ? ` — "${post.texto}"` : '';
  const link = post.link ? ` — ${post.link}` : '';
  return `${cabeza}: ${partes.length ? partes.join(' · ') : 'sin números de Meta'}${texto}${link}`;
}

const ORDEN_EN_PALABRAS: Record<string, string> = {
  recientes: 'más recientes',
  vistas: 'con más vistas',
  alcance: 'con más alcance',
  interacciones: 'con más interacciones',
  guardados: 'más guardados',
  compartidos: 'más compartidos',
};

export function textoDePosts(r: RespuestaDeMetricas): string {
  const problema = problemaDeLaCuenta(r);
  if (problema) return problema;

  const posts = (r.posts as Json[] | undefined) ?? [];
  const periodo = typeof r.periodo_dias === 'number' ? ` de los últimos ${r.periodo_dias} días` : '';
  if (!posts.length) {
    return `No hay posts${periodo} en @${r.usuario ?? 'la cuenta'}. Las historias no se ven por acá.`;
  }
  const orden = String(r.orden ?? 'recientes');
  const entre = orden === 'recientes' ? '' : ` (entre los últimos ${numero(r.revisados)} posts)`;
  const lineas = [
    `Los ${posts.length} posts ${ORDEN_EN_PALABRAS[orden] ?? ''}${periodo} de @${r.usuario ?? 'la cuenta'}${entre}:`,
    ...posts.map((p, i) => lineaDePost(p, i + 1)),
  ];
  if (r.aviso_permiso) lineas.push(String(r.aviso_permiso));
  if (r.aviso) lineas.push(String(r.aviso));
  lineas.push('Cuando nombres un post, pasá su link para que lo pueda abrir.');
  return lineas.join('\n');
}

const GENERO: Record<string, string> = { F: 'mujeres', M: 'varones', U: 'sin dato' };

function nombreDePais(codigo: string): string {
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' }).of(codigo) ?? codigo;
  } catch {
    return codigo;
  }
}

export function textoDelPublico(r: RespuestaDeMetricas): string {
  const problema = problemaDeLaCuenta(r);
  if (problema) return problema;

  const pub = (r.publico ?? {}) as Record<string, Array<{ valor: string; pct: number }>>;
  const fila = (titulo: string, lista: Array<{ valor: string; pct: number }> | undefined, nombre: (v: string) => string) =>
    lista?.length ? `• ${titulo}: ${lista.map((x) => `${nombre(x.valor)} ${cambio(x.pct).replace('+', '')}`).join(' · ')}` : null;

  return [
    `Quiénes siguen a @${r.usuario ?? 'la cuenta'} (datos de Meta, sobre el total de seguidores):`,
    fila('Países', pub.paises, nombreDePais),
    fila('Ciudades', pub.ciudades, (v) => v),
    fila('Edades', pub.edades, (v) => `${v} años`),
    fila('Género', pub.genero, (v) => GENERO[v] ?? v),
  ]
    .filter(Boolean)
    .join('\n');
}

// ── Resolvers ───────────────────────────────────────────────────────────────

export interface DependenciasDeInstagram {
  consultar: (pedido: PedidoDeMetricas) => Promise<RespuestaDeMetricas>;
  /** La conexión venció a mitad de charla: el chat deja de mandar las herramientas. */
  alVencer?: () => void;
}

const numeroDe = (v: unknown): number | undefined => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
};

async function consultarYFormatear(
  pedido: PedidoDeMetricas,
  deps: DependenciasDeInstagram,
  formatear: (r: RespuestaDeMetricas) => string,
): Promise<{ __texto: string; __error?: true }> {
  let r: RespuestaDeMetricas;
  try {
    r = await deps.consultar(pedido);
  } catch {
    return {
      __texto: 'No pude comunicarme con Instagram en este momento. Decile que pruebe de nuevo en un rato; no inventes cifras.',
      __error: true,
    };
  }
  if (r.estado === 'vencida' || !r.conectada) deps.alVencer?.();
  return { __texto: formatear(r) };
}

/** Lo que manda el modelo puede venir con cualquier forma: se lee campo por campo. */
const comoObjeto = (input: unknown): Record<string, unknown> =>
  input && typeof input === 'object' ? (input as Record<string, unknown>) : {};

export function resolverMetricasDeInstagram(input: unknown, deps: DependenciasDeInstagram) {
  return consultarYFormatear({ accion: 'cuenta', dias: numeroDe(comoObjeto(input).dias) }, deps, textoDeLaCuenta);
}

export function resolverPostsDeInstagram(input: unknown, deps: DependenciasDeInstagram) {
  const i = comoObjeto(input);
  return consultarYFormatear(
    {
      accion: 'posts',
      orden: typeof i.orden === 'string' ? i.orden : 'recientes',
      cantidad: numeroDe(i.cantidad),
      dias: numeroDe(i.dias) ?? null,
    },
    deps,
    textoDePosts,
  );
}

export function resolverPublicoDeInstagram(_input: unknown, deps: DependenciasDeInstagram) {
  return consultarYFormatear({ accion: 'publico' }, deps, textoDelPublico);
}
